import { includes, isDefined } from '@hover/utilities';
import type { TFunction } from 'i18next';
import type {
  AiImageInstanceEdit,
  AiImageRegionEdits,
  AiImageWallEdits,
} from 'mock/generated/types';

import type {
  AiOrSampleGeneratedImage,
  DesignerProductCatalogProduct,
} from 'src/modules/homeownerApp/types';
import { isAiGenerated, isDisplay } from 'src/modules/homeownerApp/types';
import { useProductCatalogVariationsQuery } from 'src/shared/graphql/generated';
import type {
  AiDisplayImageFragment,
  AiImageEdits,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import type { Region } from '../constants/customizationCategories';
import { warn } from '../helpers/logging';

export type RegionWithDisabledRegions = Region | 'fence';

// This is effectively camelcased RegionWithDisabledRegions
type AiImageEditsKeysWithoutMetadata = Omit<
  AiImageEdits,
  'schemaVersion' | '__typename'
>;

export interface VariationEdit {
  variationUuid?: string;
  region?: string;
  name?: string | null;
  manufacturer?: string;
  manufacturerUrl?: string;
  palette?: string;
  thumbnail?: string;
  color?: string;
  kind?: 'paint' | 'material';
  image?: string;
  uid: string;
  assets?: AiDisplayImageFragment[];
}

const SurfaceClassToRegion: Record<
  string,
  RegionWithDisabledRegions | undefined
> = {
  Door: 'door',
  Wall: 'walls',
  Roof: 'roof',
  Window: 'windows',
  Shutter: 'shutter',
  GarageDoor: 'garage_door',
  Soffit: 'trim',
  Fascia: 'trim',
  WindowTrim: 'trim',
  DoorTrim: 'trim',
  OtherTrim: 'trim',
};

export const mapRegionToDisplayText = (
  t: TFunction,
  region: RegionWithDisabledRegions,
) => t(`regions.${region}`);

const getSnakecaseRegionFromKey = (
  camelCaseRegion: keyof AiImageEdits,
): RegionWithDisabledRegions | undefined => {
  // TODO: handle `instances` edit model
  if (includes(['__typename', 'schemaVersion', 'instances'], camelCaseRegion)) {
    return undefined;
  }
  let region: RegionWithDisabledRegions;
  switch (camelCaseRegion) {
    case 'garageDoor':
      region = 'garage_door';
      break;
    default:
      region = camelCaseRegion as RegionWithDisabledRegions;
      break;
  }

  return region;
};

export const useVariationEdits = (
  images: (AiOrSampleGeneratedImage | AiDisplayImageFragment)[],
) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const allVariationEdits: { [key: string]: VariationEdit } = {};

  const addVariationEdit = (
    edit: AiImageRegionEdits | AiImageWallEdits | AiImageInstanceEdit,
    region: RegionWithDisabledRegions,
    image: AiOrSampleGeneratedImage | AiDisplayImageFragment,
  ) => {
    const regionDisplayName = mapRegionToDisplayText(t, region);

    if ('paint' in edit && edit?.paint) {
      const paintSwatchKey = `${edit.paint.hex}${region}`;
      if (allVariationEdits[paintSwatchKey] && isDisplay(image)) {
        if (!allVariationEdits[paintSwatchKey].assets?.includes(image)) {
          allVariationEdits[paintSwatchKey].assets?.push(image);
        }
      } else {
        allVariationEdits[paintSwatchKey] = {
          variationUuid: edit.paint.productCatalogVariationUuid || undefined,
          region: regionDisplayName,
          kind: 'paint',
          name: edit.paint.name || undefined,
          manufacturer: edit.paint.manufacturer || undefined,
          thumbnail: isDisplay(image)
            ? image.aiGeneratedImage.generatedImagePath
            : undefined,
          color: edit.paint.hex,
          uid: `${regionDisplayName}-${
            isDisplay(image) ? image.aiGeneratedImage.id : image.id || ''
          }-${edit.paint.hex}`,
          assets: isDisplay(image) ? [image] : undefined,
        };
      }
    }

    if ('material' in edit && edit?.material) {
      // TODO(MEOW-5853): we'll be removing `AiAvailableConfiguration` products
      // altogether in short order, but for now we've made `name` nullable as a
      // result of MEOW-6057 and we're not *quite* ready to remove the special
      // testing case in `useIsProductCatalogPromptsEnabled`. Users are already
      // on Product Catalog data so actual edits will always have a variation
      // UUID, it's just not represented that way in the type. We'll be
      // refactoring and removing most of this code once we've removed that
      // special case from `useIsProductCatalogPromptsEnabled`.
      const materialSwatchKeyIdentifier =
        edit.material.name || edit.material.productCatalogVariationUuid;

      if (!materialSwatchKeyIdentifier) return;

      const materialSwatchKey = `${
        edit.material.hex || ''
      }:${materialSwatchKeyIdentifier}:${region}`;
      if (allVariationEdits[materialSwatchKey] && isDisplay(image)) {
        if (!allVariationEdits[materialSwatchKey].assets?.includes(image)) {
          allVariationEdits[materialSwatchKey].assets?.push(image);
        }
      } else {
        allVariationEdits[materialSwatchKey] = {
          variationUuid: edit.material.productCatalogVariationUuid || undefined,
          region: regionDisplayName,
          palette: edit.material.palette || undefined,
          kind: 'material',
          name: edit.material.name,
          thumbnail: isDisplay(image)
            ? image.aiGeneratedImage.generatedImagePath
            : undefined,
          color: edit.material.hex || undefined,
          image: edit.material.thumbnail || undefined,
          uid: `${regionDisplayName}-${
            isDisplay(image) ? image.aiGeneratedImage.id : image.id || ''
          }-${materialSwatchKeyIdentifier}`,
          assets: isDisplay(image) ? [image] : undefined,
        };
      }
    }
  };

  images.forEach(image => {
    const allEdits =
      isAiGenerated(image) && !!image.metadata?.allEdits
        ? image.metadata.allEdits
        : isDisplay(image) && (image.aiGeneratedImage.metadata?.allEdits || {});

    Object.keys(allEdits).forEach(key => {
      const region = getSnakecaseRegionFromKey(key as keyof AiImageEdits);

      if (!region && key !== 'instances') return;

      const edit = (allEdits as AiImageEdits)[
        key as keyof AiImageEditsKeysWithoutMetadata
      ];

      if (!edit) return;

      if (Array.isArray(edit)) {
        edit.forEach(instanceEdit => {
          const instanceRegion =
            SurfaceClassToRegion[instanceEdit.surfaceClass];
          if (!instanceRegion) return;

          addVariationEdit(instanceEdit, instanceRegion, image);
        });
      } else if (region) {
        addVariationEdit(edit, region, image);
      } else {
        warn(`No region available for edit: ${JSON.stringify(edit)}`);
      }
    });
  });

  const variationUuids = Object.values(allVariationEdits)
    .map(e => e.variationUuid)
    .filter(value => value !== null && value !== undefined);
  const { data } = useProductCatalogVariationsQuery({
    variables: {
      variationIds: variationUuids,
    },
    skip: variationUuids.length === 0,
  });

  data?.productCatalogVariations.nodes?.forEach(n => {
    const variationEdit = Object.values(allVariationEdits).find(
      edit => edit.variationUuid === n?.id,
    );
    if (!variationEdit) return;
    variationEdit.name = n?.name;

    if (!n?.product?.provider?.name.match('Generic'))
      variationEdit.manufacturer = n?.product?.provider?.name;

    variationEdit.manufacturerUrl = n?.manufacturerUrl ?? undefined;
    variationEdit.palette = n?.variationFacets?.[0].facetValue?.value;
    variationEdit.image = n?.image?.redirectUrl;
  });

  return allVariationEdits;
};

export const useMaterialCallouts = (
  images: (AiOrSampleGeneratedImage | AiDisplayImageFragment)[],
) => {
  const allVariationEdits = useVariationEdits(images);

  return Object.values(allVariationEdits)
    .map(variationEdit => {
      const {
        kind,
        color,
        name,
        manufacturer,
        palette,
        region,
        uid,
        manufacturerUrl,
        image,
      } = variationEdit;

      const palettes = palette ? [palette] : [];
      const regions = region ? [region] : [];
      const baseCallout = {
        primaryLabel: name!,
        regions,
        palettes,
        id: uid,
        productCatalogVariationUuid: uid,
        manufacturerUrl,
      };

      if (kind === 'paint') {
        return {
          ...baseCallout,
          $type: 'color',
          hex: color!,
          secondaryLabel: manufacturer,
          paintRegions: regions,
          manufacturer,
        } as DesignerProductCatalogProduct;
      }
      if (kind === 'material') {
        return {
          ...baseCallout,
          $type: 'material',
          thumbnail: image!,
          aiPromptName: 'ai-prompt-name',
          is3dFusion: false,
          secondaryLabel: palette,
          spaceType: '',
          manufacturer,
        } as DesignerProductCatalogProduct;
      }

      return null;
    })
    .filter(isDefined);
};
